import { Input, Modal, Form, Radio, Card, Row, Col, DatePicker, Select, Space, InputNumber, Avatar, List, Tag, Collapse, Checkbox, Tour, Button, Tooltip } from "antd"
import TimePicker from "../../common/TimePicker"
import DurationPicker from "../../common/DurationPicker"
import { Duration, durationToHmm } from "../../common/durationModel"
import { useState, useEffect, useRef } from "react"
import dayjs from "dayjs"
import { useCreateRouteMutation } from "../routingQueries"
import { CloseCircleOutlined } from "@ant-design/icons"
import { CreateRouteRecordRequest, CrewMember, EndType, RoutingType } from "../routingApi"
import { useTenantWorkDaySettingsDataQuery } from "../../../domain/tenantWorkDaySettings/tenantWorkDaySettingsQueries"
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

export type Props = {
  isOpen: boolean
  onClose: (routeId?: number) => void
  crew: CrewMember[] | undefined
  onSave?: (routeId: number) => void
}

const CreateRouteModal = ({ isOpen, onClose, crew, onSave }: Props) => {
  const [routeType, setRouteType] = useState<'onDemand' | 'scheduled'>('scheduled')
  const [endType, setEndType] = useState<'never' | 'on' | 'after'>('on')
  const [selectedCrew, setSelectedCrew] = useState<any[]>([])
  const [crewLeadId, setCrewLeadId] = useState<number | null>(null)
  const [selectedDays, setSelectedDays] = useState<string>('Sunday')
  const [workDaySettingsExpanded, setWorkDaySettingsExpanded] = useState<boolean>(false)
  const [form] = Form.useForm()
  const routeNameInputRef = useRef<any>(null)
  const viewed = window.localStorage.getItem('create-route-modal-tour-viewed') === 'true'
  const [isTourOpen, setIsTourOpen] = useState(!viewed)
  const createRouteMutation = useCreateRouteMutation()
  const { data: tenantWorkDaySettings, isLoading } = useTenantWorkDaySettingsDataQuery()

  const getWorkDaySettings = () => {
    if (tenantWorkDaySettings) {
      const workDayData = Array.isArray(tenantWorkDaySettings) 
        ? tenantWorkDaySettings[0] 
        : tenantWorkDaySettings
      
      if (workDayData) {
        const parseTimeFromDateTime = (datetimeString: string) => {
          if (!datetimeString.includes('T')) {
            const timeOnly = dayjs(datetimeString, 'HH:mm:ss')
            if (timeOnly.isValid()) {
              return timeOnly
            }
          }
          
          const fullDateTime = dayjs(datetimeString)
          if (fullDateTime.isValid()) {
            const timeMatch = datetimeString.match(/T(\d{2}):(\d{2}):(\d{2})/)
            if (timeMatch) {
              const [, hours, minutes] = timeMatch
              
              const result = dayjs().hour(parseInt(hours)).minute(parseInt(minutes)).second(0).millisecond(0)
              return result
            }
          }
          
          return null
        }
        
        const dayStart = parseTimeFromDateTime(workDayData.dayStartTime)
        const anticipatedDayEnd = parseTimeFromDateTime(workDayData.dayEndTime)
        const estimatedBreakTime: Duration = { seconds: (workDayData.estimatedBreakTimeMinutes ?? 0) * 60 }

        return {
          dayStart: dayStart || dayjs('08:00', 'HH:mm'),
          anticipatedDayEnd: anticipatedDayEnd || dayjs('16:00', 'HH:mm'),
          includeBreakTime: workDayData.hasBreakTime,
          estimatedBreakTime,
        }
      }
    }

    return {
      dayStart: dayjs('08:00', 'HH:mm'),
      anticipatedDayEnd: dayjs('16:00', 'HH:mm'),
      includeBreakTime: true,
      estimatedBreakTime: { seconds: 30 * 60 } as Duration,
    }
  }

  useEffect(() => {
    if (isOpen && !isLoading) {
      const workDaySettings = getWorkDaySettings()
      
      const defaultValues = {
        routeType: 'scheduled',
        selectedDays: 'Sunday',
        startDate: dayjs(),
        repeatUnit: 'week',
        repeatCount: 1,
        repeatPlace: ["1st"],
        repeatQuarter: [1],
        repeatMonth: [dayjs().month() + 1],
        endType: 'on',
        endDate: dayjs().add(30, 'day'),
        afterTimes: 1,
        dayStart: workDaySettings.dayStart,
        anticipatedDayEnd: workDaySettings.anticipatedDayEnd,
        includeBreakTime: workDaySettings.includeBreakTime,
        estimatedBreakTime: workDaySettings.estimatedBreakTime,
        dayStartDisplay: workDaySettings.dayStart,
        anticipatedDayEndDisplay: workDaySettings.anticipatedDayEnd,
        includeBreakTimeDisplay: workDaySettings.includeBreakTime,
      }
      form.setFieldsValue(defaultValues)
      
      // Focus the route name input after a short delay to ensure modal is fully rendered
      setTimeout(() => {
        if (routeNameInputRef.current) {
          routeNameInputRef.current.focus()
        }
      }, 100)
    }
  }, [isOpen, form, tenantWorkDaySettings, isLoading])

  const handleClose = (routeId?: number) => {
    form.resetFields()
    setRouteType('scheduled')
    onClose(routeId)
  }

  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
  
      
      let estimatedBreakTimeMinutes = 0;
      if (values.includeBreakTime && values.estimatedBreakTime) {
        estimatedBreakTimeMinutes = Math.floor(values.estimatedBreakTime.seconds / 60);
      }
      
      const request: CreateRouteRecordRequest = {
        name: values.routeName,
        crewLeadId: crewLeadId,
        crewMemberIds: selectedCrew.filter(member => member?.id).map(member => member.id).join(','),
        routeType: values.routeType === 'onDemand' ? RoutingType.OnDemand : RoutingType.Planned,
        dayStartTime: values.dayStart,
        dayEndTime: values.anticipatedDayEnd,
        estimatedBreakTimeMinutes: estimatedBreakTimeMinutes,
        hasBreakTime: values.includeBreakTime,
        routingScheduleConfig: {
          dayOfWeek: values.selectedDays,
          startDate: values.startDate,
          endDate: values.endDate,
          endType: endType === 'never' ? EndType.Never : endType === 'on' ? EndType.On : EndType.After,
          repeatCount: values.repeatCount,
          repeatUnits: values.repeatUnit,
          occurrences: values.afterTimes,
          repeatPlace: values.repeatPlace ? values.repeatPlace.join(',') : null,
          repeatQuarter: values.repeatQuarter ? values.repeatQuarter.join(',') : null,
          repeatMonth: values.repeatMonth ? values.repeatMonth.join(',') : null,
        }
      }

      createRouteMutation.mutate(request, {
        onSuccess: (routeId: number) => {
          if (onSave) {
            onSave(routeId)
          }
          handleClose(routeId)
        }
      })
    } catch (error) {
    }
  }

  const handleCrewSelect = (value: number[] | undefined) => {
    if (!value || !Array.isArray(value)) {
      setSelectedCrew([]);
      return;
    }

    const newCrewMembers = value.map(id =>
      crew?.find(member => member.id === id)
    ).filter(Boolean) as CrewMember[];

    setSelectedCrew(newCrewMembers);

    if (!crewLeadId && newCrewMembers.length > 0) {
      const firstWithEmail = newCrewMembers.find(member => member.userEmail);
      if (firstWithEmail) {
        setCrewLeadId(firstWithEmail.id);
      }
    }
  }

  const removeCrewMember = (id: any) => {
    const updatedCrew = selectedCrew.filter(member => member.id !== id);
    setSelectedCrew(updatedCrew);
    form.setFieldsValue({ crewSearch: updatedCrew.map(member => member.id) });
  }

  const setCrewLead = (id: number) => {
    if (crewLeadId === id) {
      setCrewLeadId(null);
    } else {
      setCrewLeadId(id);
    }
  }

  const tourSteps = [
    {
      title: 'Tour Guide',
      description: 'Click this icon on any routing screen to restart the tour if you need a refresher on the features.',
      target: () => document.getElementById('tour-help-button') as HTMLElement || null,
    },
    {
      title: 'Route Name',
      description: 'Give your route a descriptive name to help identify it in your route list.',
      target: () => document.getElementById('route-name-input') as HTMLElement || null,
    },
    {
      title: 'Day of the Week',
      description: 'Select which day of the week this route will run. This determines when your crew will follow this planned route.',
      target: () => document.getElementById('day-of-week-selector') as HTMLElement || null,
    },
    {
      title: 'Schedule Settings',
      description: 'Configure when your route starts, how often it repeats, and when it ends. This creates a recurring schedule for your route.',
      target: () => document.getElementById('schedule-settings') as HTMLElement || null,
    },
    {
      title: 'Work Day Settings',
      description: 'Set your work day hours and break times. This helps the system calculate route capacity and timing accurately.',
      target: () => document.getElementById('work-day-settings') as HTMLElement || null,
    },
    {
      title: 'Crew Assignment',
      description: 'Select your entire team.  When you dismiss the dropdown you will be able to designate a crew lead by clicking "Set as Crew Lead".',
      target: () => document.getElementById('crew-settings') as HTMLElement || null,
    },
  ];

  return <Modal 
    open={isOpen} 
    onCancel={() => handleClose()} 
    onOk={handleCreate}
    okText="Create"
    width={1000}
    title=<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>
        Create New Route      
        <Tooltip title="Need help? Take a quick tour of the route creation features"> 
        <Button 
          id="tour-help-button"
          type="text"
          size="small"
          onClick={() => setIsTourOpen(true)}
          title="Take a quick tour of the route creation features"
          style={{ marginRight: -8 }}
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </Button>
        </Tooltip>
      </span>
    </div>
    >
      <Form 
        form={form}
        layout="vertical"
      >
        {/* Hidden fields to ensure values are always registered with the form */}
        <Form.Item name="dayStart" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="anticipatedDayEnd" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="includeBreakTime" valuePropName="checked" hidden>
          <Input />
        </Form.Item>

        <Form.Item 
          label="Route Name" 
          name="routeName"
          rules={[
            { required: true, message: 'Please enter a route name' },
            { max: 150, message: 'Route name cannot exceed 150 characters' }
          ]}
        >
          <Input 
            id="route-name-input"
            ref={routeNameInputRef}
            placeholder="Route Name" 
            maxLength={150}
            showCount
          />
        </Form.Item>
        <Row gutter={16}>
          <Col span={13}>
            <Form.Item
              label="Route Type"
              name="routeType"
              initialValue="scheduled"
            >
              <Card>
                <Radio.Group
                  value={routeType}
                  onChange={(e) => setRouteType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Row gutter={24}>
                    <Col span={12}>
                      <Radio value="scheduled">Planned</Radio>
                      <div style={{ marginLeft: 24, color: '#8c8c8c', fontSize: 12 }}>
                        Recurring routes set on specific days, automatically.
                      </div>
                    </Col>
                    <Col span={12}>
                      <Radio value="onDemand">On-Demand</Radio>
                      <div style={{ marginLeft: 24, color: '#8c8c8c', fontSize: 12 }}>
                        Routes you add whenever needed, instantly.
                      </div>
                    </Col>
                  </Row>
                </Radio.Group>
              </Card>
            </Form.Item>
            
            {routeType === 'scheduled' && (
              <>
                <Form.Item 
                  label="Planned Weekday" 
                  name="selectedDays"
                  initialValue="Sunday"
                >
                  <Card id="day-of-week-selector" style={{ padding: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Radio.Group 
                        buttonStyle="solid"
                        value={selectedDays}
                        onChange={(e) => {
                          setSelectedDays(e.target.value)
                          form.setFieldsValue({ selectedDays: e.target.value })
                        }}
                      >
                        <Space size="middle">
                          {[
                            { key: 'Sunday', label: 'S' },
                            { key: 'Monday', label: 'M' },
                            { key: 'Tuesday', label: 'T' },
                            { key: 'Wednesday', label: 'W' },
                            { key: 'Thursday', label: 'T' },
                            { key: 'Friday', label: 'F' },
                            { key: 'Saturday', label: 'S' }
                          ].map((day) => (
                            <Radio.Button 
                              key={day.key} 
                              value={day.key}
                              style={{ 
                                width: '55px',
                                height: '40px',
                                borderRadius: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              {day.label}
                            </Radio.Button>
                          ))}
                        </Space>
                      </Radio.Group>
                    </div>
                  </Card>
                </Form.Item>
                
                <Form.Item label="Default Schedule">
                  <Card id="schedule-settings">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Form.Item 
                        label="Start Date" 
                        name="startDate"
                        style={{ marginBottom: 16 }}
                        initialValue={dayjs()}
                        rules={[
                          { required: true, message: 'Please select a start date' }
                        ]}
                      >
                        <DatePicker 
                          style={{ width: '100%' }} 
                          format="MMMM D, YYYY"
                        />
                      </Form.Item>
                      
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ marginRight: 10 }}>Repeats</span>
                          <Form.Item
                            noStyle
                            name="repeatUnit"
                            initialValue="week"
                          >
                            <Select style={{ width: 200 }}>
                              <Select.Option value="week">Weekly</Select.Option>
                              <Select.Option value="month">Monthly</Select.Option>
                              <Select.Option value="quarter">Quarterly</Select.Option>
                              <Select.Option value="year">Annually</Select.Option>
                            </Select>
                          </Form.Item>
                        </div>
                        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.repeatUnit !== currentValues.repeatUnit}>
                          {({ getFieldValue }) => {
                            const repeatUnit = getFieldValue('repeatUnit');
                            switch (repeatUnit) {
                              case 'week':
                                return (
                                  <Space>
                                    Every <Form.Item
                                      noStyle
                                      name="repeatCount"
                                      initialValue={1}
                                      rules={[{ required: true, message: 'Please enter repeat count' }]}
                                    >
                                      <InputNumber min={1} style={{ width: 60 }} />
                                    </Form.Item>
                                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.repeatUnit !== currentValues.repeatUnit}>
                                      {({ getFieldValue }) => {
                                        const repeatUnit = getFieldValue('repeatUnit');
                                        return repeatUnit === 'week' ? 'week' : null;
                                      }}
                                    </Form.Item>
                                  </Space>
                                );
                              case 'month':
                                return (
                                  <Space>
                                    On the <Form.Item
                                      noStyle
                                      name="repeatPlace"
                                      initialValue={["1st"]}
                                      rules={[{ required: true, message: 'Please select repeat place' }]}
                                    >
                                      <Select 
                                        style={{ width: 120 }}
                                        mode="multiple"
                                        placeholder="Select"
                                        maxTagCount={3}
                                        maxTagTextLength={10}
                                      >
                                        <Select.Option value="1st">1st</Select.Option>
                                        <Select.Option value="2nd">2nd</Select.Option>
                                        <Select.Option value="3rd">3rd</Select.Option>
                                        <Select.Option value="4th">4th</Select.Option>
                                        <Select.Option value="last">Last</Select.Option>
                                      </Select>
                                    </Form.Item> {(() => {
                                      const dayMap: Record<string, string> = {
                                        'S': 'Sunday',
                                        'M': 'Monday', 
                                        'T': 'Tuesday',
                                        'W': 'Wednesday',
                                        'T2': 'Thursday',
                                        'F': 'Friday',
                                        'S2': 'Saturday'
                                      };
                                      return dayMap[selectedDays] || selectedDays;
                                    })()} of the month
                                  </Space>
                                );
                              case 'quarter':
                                return (
                                  <Space>
                                    On the <Form.Item
                                      noStyle
                                      name="repeatPlace"
                                      initialValue={["1st"]}
                                      rules={[{ required: true, message: 'Please select repeat place' }]}
                                    >
                                      <Select
                                        style={{ width: 120 }}
                                        mode="multiple"
                                        placeholder="Select"
                                        maxTagCount={3}
                                        maxTagTextLength={10}
                                      >
                                        <Select.Option value="1st">1st</Select.Option>
                                        <Select.Option value="2nd">2nd</Select.Option>
                                        <Select.Option value="3rd">3rd</Select.Option>
                                        <Select.Option value="4th">4th</Select.Option>
                                        <Select.Option value="last">Last</Select.Option>
                                      </Select>
                                    </Form.Item> {(() => {
                                      const dayMap: Record<string, string> = {
                                        'S': 'Sunday',
                                        'M': 'Monday', 
                                        'T': 'Tuesday',
                                        'W': 'Wednesday',
                                        'T2': 'Thursday',
                                        'F': 'Friday',
                                        'S2': 'Saturday'
                                      };
                                      return dayMap[selectedDays] || selectedDays;
                                    })()} of the <Form.Item
                                      noStyle
                                      name="repeatQuarter"
                                      initialValue={[1]}
                                      rules={[{ required: true, message: 'Please select quarter' }]}
                                    >
                                      <Select
                                        style={{ width: 120 }}
                                        mode="multiple"
                                        placeholder="Select"
                                        maxTagCount={3}
                                        maxTagTextLength={10}
                                      >
                                        <Select.Option value={1}>1st</Select.Option>
                                        <Select.Option value={2}>2nd</Select.Option>
                                        <Select.Option value={3}>3rd</Select.Option>
                                        <Select.Option value={4}>4th</Select.Option>
                                      </Select>
                                    </Form.Item> quarter
                                  </Space>
                                );
                              case 'year':
                                return (
                                  <Space>
                                    On the <Form.Item
                                      noStyle
                                      name="repeatPlace"
                                      initialValue={["1st"]}
                                      rules={[{ required: true, message: 'Please select repeat place' }]}
                                    >
                                      <Select
                                        style={{ width: 120 }}
                                        mode="multiple"
                                        placeholder="Select"
                                        maxTagCount={3}
                                        maxTagTextLength={10}
                                      >
                                        <Select.Option value="1st">1st</Select.Option>
                                        <Select.Option value="2nd">2nd</Select.Option>
                                        <Select.Option value="3rd">3rd</Select.Option>
                                        <Select.Option value="4th">4th</Select.Option>
                                        <Select.Option value="last">Last</Select.Option>
                                      </Select>
                                    </Form.Item> {(() => {
                                      const dayMap: Record<string, string> = {
                                        'S': 'Sunday',
                                        'M': 'Monday', 
                                        'T': 'Tuesday',
                                        'W': 'Wednesday',
                                        'T2': 'Thursday',
                                        'F': 'Friday',
                                        'S2': 'Saturday'
                                      };
                                      return dayMap[selectedDays] || selectedDays;
                                    })()} of <Form.Item
                                      noStyle
                                      name="repeatMonth"
                                      initialValue={[dayjs().month() + 1]}
                                      rules={[{ required: true, message: 'Please select month' }]}
                                    >
                                      <Select
                                        style={{ width: 120 }}
                                        mode="multiple"
                                        placeholder="Select"
                                        maxTagCount={3}
                                        maxTagTextLength={10}
                                      >
                                        <Select.Option value={1}>January</Select.Option>
                                        <Select.Option value={2}>February</Select.Option>
                                        <Select.Option value={3}>March</Select.Option>
                                        <Select.Option value={4}>April</Select.Option>
                                        <Select.Option value={5}>May</Select.Option>
                                        <Select.Option value={6}>June</Select.Option>
                                        <Select.Option value={7}>July</Select.Option>
                                        <Select.Option value={8}>August</Select.Option>
                                        <Select.Option value={9}>September</Select.Option>
                                        <Select.Option value={10}>October</Select.Option>
                                        <Select.Option value={11}>November</Select.Option>
                                        <Select.Option value={12}>December</Select.Option>
                                      </Select>
                                    </Form.Item>
                                  </Space>
                                );
                              default:
                                return null;
                            }
                          }}
                        </Form.Item>
                      </div>

                      <div>
                        <div style={{ marginBottom: 8 }}>Ends</div>
                        <Form.Item
                          noStyle
                          name="endType"
                          rules={[{ required: true, message: 'Please select end type' }]}
                          initialValue="on"
                        >
                          <Radio.Group 
                            onChange={(e) => {
                              setEndType(e.target.value)
                            }}
                          >
                            <Space direction="vertical">
                              <Radio value="on">
                                On <Form.Item
                                  noStyle
                                  name="endDate"
                                  initialValue={dayjs().add(30, 'day')}
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        if(endType !== 'on') {
                                          return Promise.resolve();
                                        }
                                        if(endType === 'on' && value === null) {
                                          return Promise.reject(new Error('End date is required'));
                                        }
                                        const startDate = getFieldValue('startDate');
                                        if(endType === 'on' && startDate && value && value.isBefore(startDate)) {
                                          return Promise.reject(new Error('End date cannot be before start date'));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <DatePicker 
                                    style={{ width: 200 }} 
                                    format="MMMM D, YYYY"
                                    disabled={endType !== 'on'}
                                  />
                                </Form.Item>
                              </Radio>
                              <Radio value="after">
                                After <Form.Item
                                  noStyle
                                  name="afterTimes"
                                  initialValue={1}
                                  rules={[
                                    () => ({
                                      validator(_, value) {
                                        if (endType !== 'after') {
                                          return Promise.resolve();
                                        }
                                        if (endType === 'after' && !value) {
                                          return Promise.reject(new Error('Please enter number of times'));
                                        }
                                        if (endType === 'after' && (!Number.isInteger(value) || value < 1)) {
                                          return Promise.reject(new Error('Must be a positive whole number'));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <InputNumber 
                                    style={{ width: 80 }} 
                                    disabled={endType !== 'after'}
                                    min={1}
                                  />
                                </Form.Item> times
                              </Radio>
                              {/* <Radio value="never">Never</Radio> */}
                            </Space>
                          </Radio.Group>
                        </Form.Item>
                      </div>
                    </Space>
                  </Card>
                </Form.Item>
              </>
            )}
            <Form.Item label="Work Day Settings">
              <Card id="work-day-settings">
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (
                    <Collapse
                      ghost
                      size="small"
                      activeKey={workDaySettingsExpanded ? ['workday'] : []}
                      onChange={(keys) => setWorkDaySettingsExpanded(keys.includes('workday'))}
                      expandIconPosition="end"
                      items={[
                        {
                          key: 'workday',
                          label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                              <div>
                                {!workDaySettingsExpanded ? (
                                  <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                    {!isLoading && (
                                      <>
                                        {(() => {
                                          const workDaySettings = getWorkDaySettings()
                                          const dayStart = getFieldValue('dayStart') || workDaySettings.dayStart
                                          const dayEnd = getFieldValue('anticipatedDayEnd') || workDaySettings.anticipatedDayEnd
                                          const includeBreak = getFieldValue('includeBreakTime') ?? workDaySettings.includeBreakTime
                                          const breakTime = getFieldValue('estimatedBreakTime') || workDaySettings.estimatedBreakTime
                                          
                                          return (
                                            <>
                                              {dayStart.format('h:mm A')} - {dayEnd.format('h:mm A')}
                                              {includeBreak && (
                                                <>
                                                  <br />
                                                  <span style={{ color: '#8c8c8c' }}>Estimated Break Time:</span> {durationToHmm(breakTime)}
                                                </>
                                              )}
                                            </>
                                          )
                                        })()}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                    These settings will allow you to determine route capacity
                                  </div>
                                )}
                              </div>
                            </div>
                          ),
                          children: (
                            <div>
                               <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    label="Day Start"
                                    name="dayStartDisplay"
                                  >
                                    <TimePicker
                                      onChange={(value) => {
                                        form.setFieldsValue({ dayStart: value })
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label="Anticipated Day End"
                                    name="anticipatedDayEndDisplay"
                                  >
                                    <TimePicker
                                      onChange={(value) => {
                                        form.setFieldsValue({ anticipatedDayEnd: value })
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                              
                              <Row>
                                <Col span={24}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <Form.Item
                                      name="includeBreakTimeDisplay"
                                      valuePropName="checked"
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Checkbox
                                        onChange={(e) => {
                                          form.setFieldsValue({ includeBreakTime: e.target.checked })
                                        }}
                                      >
                                        Estimated Break Time
                                      </Checkbox>
                                    </Form.Item>
                                    <Form.Item
                                      name="estimatedBreakTime"
                                      initialValue={{ seconds: 30 * 60 }}
                                      style={{ marginBottom: 0, flex: 1 }}
                                    >
                                      <DurationPicker increment={900} />
                                    </Form.Item>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          )
                        }
                      ]}
                    />
                  )}
                </Form.Item>
              </Card>
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item label="Crew">
              <Card id="crew-settings">
                <Space direction="vertical" style={{ width: '100%', minHeight: 250 }}>
                  <Form.Item
                    name="crewSearch"
                  >
                    <Select
                      showSearch
                      placeholder="Search and select crew members"
                      optionFilterProp='label'
                      style={{ width: '100%' }}
                      onChange={handleCrewSelect}
                      value={selectedCrew?.map(member => member?.id).filter(Boolean) || []}
                      allowClear
                      mode="multiple"
                      maxTagCount={3}
                      maxTagTextLength={10}
                    >
                      {crew?.map((member) => (
                        <Select.Option 
                          key={member.id} 
                          value={member.id}
                          label={member.name}
                        >
                          <Space>
                            <Avatar src={member.avatar} size="small">
                              {member.name.charAt(0)}
                            </Avatar>
                            <span>{member.name}</span>
                          </Space>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  
                  {selectedCrew.length > 0 ? (
                    <List
                      size="small"
                      dataSource={selectedCrew}
                      renderItem={(member) => (
                        <List.Item
                          actions={[
                            !member.userEmail ? (
                              <Tooltip key="setLead" title="Crew member needs an email address to be a crew lead">
                                <Button
                                  type="link"
                                  size="small"
                                  disabled
                                  style={{
                                    padding: 0,
                                    cursor: 'not-allowed'
                                  }}
                                >
                                  Set as Crew Lead
                                </Button>
                              </Tooltip>
                            ) : (
                              <a
                                key="setLead"
                                onClick={() => setCrewLead(member.id)}
                                style={{ color: crewLeadId === member.id ? '#faad14' : '#1890ff' }}
                              >
                                {crewLeadId === member.id ? "" : "Set as Crew Lead"}
                              </a>
                            ),
                            <a key="remove" onClick={() => removeCrewMember(member.id)}>
                              <CloseCircleOutlined style={{ fontSize: '16px' }} />
                            </a>
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              <Avatar src={member.avatar} size="small">
                                {member.name.charAt(0)}
                              </Avatar>
                            }
                            title={
                              <Space>
                                {member.name}
                                {crewLeadId === member.id && (
                                  <Tag color="gold">Crew Lead</Tag>
                                )}
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <div style={{ 
                      border: '1px dashed #d9d9d9', 
                      borderRadius: '8px',
                      padding: '40px 0',
                      textAlign: 'center',
                      color: '#666',
                      minHeight: 250,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      No Assigned Crew
                    </div>
                  )}
                </Space>
              </Card>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      
      <Tour
        open={isTourOpen && isOpen}
        closable={true}
        onClose={() => {
          setIsTourOpen(false)
          window.localStorage.setItem('create-route-modal-tour-viewed', 'true')
        }}
        onFinish={() => {
          setIsTourOpen(false)
          window.localStorage.setItem('create-route-modal-tour-viewed', 'true')
        }}
        steps={tourSteps.filter(step => step.target() !== null)}
        type="default"
        disabledInteraction={true}
      />
  </Modal>

}

export default CreateRouteModal;